/* eslint-disable no-restricted-globals */
// ╔════════════════════════════════════════════════════════════════════════════╗
// ║  APP.JS - VERSION: 2026.01.18.1830                                          ║
// ╚════════════════════════════════════════════════════════════════════════════╝
// ============================================================================
// 📦 VERSION: 2026.01.18.1830
// ============================================================================
// CHANGELOG:
// 2026.01.18.1830 - FIX: Guest stats sync uses POST not PUT (404 bug)
// 2026.01.18.1400 - FIX: Remove double bonus - bonus ONLY added when reward claimed (not on unlock)
// 2026.01.17.1630 - FIX: Milestone bonuses now add TRAKs correctly (was adding to tags)
// 2026.01.17.1630 - FIX: Popup now shows "+X TRAKs!" instead of "+X tags!"
// 2026.01.17.1600 - FIX: Guest registration now transfers ALL accumulated localStorage stats
// 2026.01.15.0830 - PERF: Lazy load pictures for selected game only (faster startup)
// 2026.01.14.1415 - Added checkPalReferralBonus for Spin to Win signup flow
// 2026.01.12.1230 - SEO FIX: Canonical now uses useLocation, quiz pages handled by QuizSEO
// 2026.01.11.1200 - FIX: Transform game data snake_case→camelCase for banner URLs
// 2026.01.09.1000 - SEO: Added QuizSEO component for dynamic meta tags per quiz
// 2025.12.30.2245 - Added ParentQuizPage for category pages (All About Trains etc)
// 2025.12.30.2130 - SEO: Added dynamic canonical URL based on current page to fix duplicate content
// 2025.12.22.1135 - FIX: Syntax error (extra closing brace)
// 2025.12.22.1130 - FIX: Added delay to autoOpenGameId to ensure QuizzesTab mounts first
// 2025.12.22.1030 - Slug URLs (/quiz/name) now auto-open quiz bottom sheet
// 2025.12.22.0955 - Deep link now auto-opens quiz bottom sheet
// 2025.12.22.0935 - Added ?game= URL parameter for deep linking to specific quizzes
// 2025.12.16.0045 - FIX: Removed duplicate BrowserRouter (already in index.js)
// 2025.12.16.0030 - SEO: Added /quiz/:slug URL handling for Google indexing
// 2025.12.14.1910 - Moved Login/Profile button to top right
// 2025.12.14.1845 - FIX: Group Quiz now works - pass handleStartGroupMode to HomeTab
// 2025.12.14.1700 - Notifications now fetch from API
// 2025.12.14.1700 - Red dot on profile button when unread notifications
// 2025.12.14.1600 - Added FloatingContactButton (always visible, sends to admin messages)
// 2025.12.13.1545 - CRITICAL FIX: Duplicate milestone rewards - now checks DB BEFORE creating
// 2025.12.12.1715 - UX: Removed questions prop from HomeTab (not needed)
// 2025.12.12.1700 - UX: Added questions, onSelectMode, timer props to HomeTab for new modal
// 2025.12.12.1600 - FIX: Guest session stats preserved when registering mid-game
// 2025.12.05.0001 - Added Reward Claiming Feature (RewardClaimModal, UnclaimedRewardsBadge)
// 2025.12.04.0001 - FIX: Session stats now update local playerData (Stats tab sync bug)
// 2025.12.04.0001 - FIX: Milestone only triggers on TRAK increase, not on login/startup
// 2025.12.04.0001 - FIX: Pass isAuthenticated to HomeTab, only show playerData when logged in
// 2025.12.02.1820 - Handedness now syncs to database, layout fixes
// 2025.12.02.1810 - Added registration prompt after 16 questions for anonymous players
// 2025.12.02.1800 - Stats now sync to database when exiting game, fixed player_id in challenges
// 2025.12.02.1700 - Challenge submission now calls API instead of just state
// 2025.12.02.1635 - Fixed ESLint import order error
// 2025.12.02.1630 - Fixed API URL to use correct server, questions now load properly
// 2025.12.02.1600 - Pass questions prop to Section2, clear grid on new game start
// 2025.12.02.1415 - Pass questions to QuizzesTab for readiness indicator
// 2025.12.02.1345 - Load pictures from API instead of localStorage
// 2025.12.01.0430 - Load games and questions from API instead of localStorage
// 2025.12.01.0345 - Removed admin panel (now separate admin app)
// 2025.11.30.2200 - Added API integration and AuthScreen for login/register
// 2025.11.30.0145 - Added pictures prop to QuizzesTab, onStartSoloMode to HomeTab
// 2025.11.29.0020 - Home page redesign integration
// 2025.11.29.0015 - handleSetFeaturedGame, pictures/iconUrl to HomeTab
// 2025.11.28.1145 - Bottom Navigation Bar system
// ============================================================================
import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';

// Navigation Components
import BottomNavBar from './components/BottomNavBar';
import HomeTab from './components/HomeTab';
import QuizzesTab from './components/QuizzesTab';
import StatsTab from './components/StatsTab';
import SettingsTab from './components/SettingsTab';

// Game Components
import Section2WithIDLabels from './components/Section2WithIDLabels';
import AuthScreen from './components/AuthScreen';

// 🆕 Pal Help Page (viral loop)
import PalHelpPage from './components/PalHelpPage';

// 📂 Parent Quiz Page (category pages with child quizzes)
import ParentQuizPage from './components/ParentQuizPage';

// 🎁 Reward Claiming Components (NEW)
import RewardClaimModal from './components/RewardClaimModal';
import UnclaimedRewardsBadge from './components/UnclaimedRewardsBadge';

// 💬 Floating Contact Button (always visible)
import FloatingContactButton from './components/FloatingContactButton';

// 🔍 SEO: Dynamic meta tags for quiz pages
import QuizSEO, { QuizSEOContent } from './components/QuizSEO';

// API
import { getToken, getProfile, getGames as fetchGamesFromAPI, getQuestions as fetchQuestionsFromAPI, isLoggedIn } from './api';

// Helpers
import { getCurrentAchievement, ACHIEVEMENT_MILESTONES, getUnclaimedMilestones } from './achievementHelpers';
import { logAchievementReward } from './rewardsApi';
// 🔧 API Configuration - Change this to match your server
const API_URL = 'https://api.traktag.com/api';
// For local development: const API_URL = 'http://localhost:3001';

// Helper to sync stats to database
const syncStatsToDatabase = async (traksEarned, tagsEarned, questionsAnswered, correctAnswers) => {
  const token = getToken();
  if (!token) return; // Not logged in, can't sync
  
  try {
    const response = await fetch(`${API_URL}/players/stats`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        traks_earned: traksEarned,
        tags_earned: tagsEarned,
        questions_answered: questionsAnswered,
        correct_answers: correctAnswers
      })
    });
    
    if (response.ok) {
      console.log('✅ Stats synced to database');
    } else {
      console.error('❌ Failed to sync stats:', await response.text());
    }
  } catch (err) {
    console.error('❌ Error syncing stats:', err);
  }
};

// ════════════════════════════════════════════════════════════════════════════
// 🆕 Check for pending pal referral bonus after registration/login
// This handles the Spin to Win flow where pal spins wheel, then registers
// ════════════════════════════════════════════════════════════════════════════
const checkPalReferralBonus = async (token) => {
  const palCode = localStorage.getItem('palReferralCode');
  const palBonus = localStorage.getItem('palBonusAmount');
  const palName = localStorage.getItem('palHelperName');
  
  // If no pending bonus, exit early
  if (!palCode || !palBonus) {
    return;
  }
  
  console.log(`🎁 Found pending pal bonus: ${palBonus} TRAKs for ${palName}`);
  
  try {
    const response = await fetch(`${API_URL}/pal/claim-referral`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        code: palCode,
        bonus_amount: parseInt(palBonus)
      })
    });
    
    const data = await response.json();
    
    if (data.success) {
      console.log(`✅ Pal bonus claimed: ${palBonus} TRAKs`);
      
      // Clear localStorage
      localStorage.removeItem('palReferralCode');
      localStorage.removeItem('palBonusAmount');
      localStorage.removeItem('palHelperName');
      
      // Show success message
      alert(`🎉 Welcome ${palName}! You earned ${palBonus} TRAKs for helping your friend!`);
    } else {
      console.error('❌ Failed to claim pal bonus:', data.error);
    }
  } catch (err) {
    console.error('❌ Error claiming pal bonus:', err);
  }
};

// ============================================================================
// MAIN APP WITH BOTTOM NAVIGATION
// ============================================================================
export default function TRAKLinkedSystem() {
  // =========================================================================
  // AUTH STATE
  // =========================================================================
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // =========================================================================
  // NAVIGATION STATE
  // =========================================================================
  const [activeTab, setActiveTab] = useState('home');  // home, quizzes, stats, settings
  const [isPlaying, setIsPlaying] = useState(false);   // True when in active game
  
  // =========================================================================
  // 🎁 REWARD CLAIMING STATE (NEW)
  // =========================================================================
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [refreshBadge, setRefreshBadge] = useState(0);  // Increment to trigger badge refresh
  
  // =========================================================================
  // GAME STATE
  // =========================================================================
  const [selectedGameId, setSelectedGameId] = useState(null);
  const [autoOpenGameId, setAutoOpenGameId] = useState(null); // 🆕 For deep link auto-open
  const [parentGame, setParentGame] = useState(null); // 📂 For parent category pages
  const [currentMode, setCurrentMode] = useState('relaxed');
  const [groupQuizMode, setGroupQuizMode] = useState(false);
  const [groupQuizTimer, setGroupQuizTimer] = useState(0);
  const [questionsInSession, setQuestionsInSession] = useState(0);
  
  // Grid state
  const [gridTappedCells, setGridTappedCells] = useState(new Set());
  const [currentPictureIndex, setCurrentPictureIndex] = useState(0);
  
  // Game editing state
  const [editingGame, setEditingGame] = useState(false);
  const [editGameData, setEditGameData] = useState(null);
  
  // Admin state removed - admin is now separate app

  // Mode statistics
  const [modeStats, setModeStats] = useState({
    relaxed: { questions: 0, correct: 0 },
    thinker: { questions: 0, correct: 0 },
    speed: { questions: 0, correct: 0 },
    lightning: { questions: 0, correct: 0 }
  });

  // 🔧 FIX 2025.12.12: Store session stats when guest registers mid-game
  const [pendingSessionStats, setPendingSessionStats] = useState(null);

  // =========================================================================
  // PLAYER DATA
  // =========================================================================
  const [playerData, setPlayerData] = useState({
    playerId: 1042,
    username: 'Player_1042',
    email: 'player@example.com',
    phoneNumber: '7700 900123',
    countryCode: '+44',
    country: 'United Kingdom',
    dateRegistered: '2024-01-15T10:30:00Z',
    lastLogin: new Date().toISOString(),
    accountStatus: 'active',
    avatar: null,
    handedness: 'right',
    stats: { totalTraks: 0, totalTags: 0, questionsAnswered: 0, correctAnswers: 0, currentStreak: 0, lastPlayDate: null, claimedMilestones: [] }
  });

  // Load player data from localStorage OR API if logged in
  useEffect(() => {
    const loadPlayerData = async () => {
      // Check if user has API token
      if (isLoggedIn()) {
        try {
          const profile = await getProfile();
          if (profile && !profile.error) {
            setIsAuthenticated(true);
            // Convert API format to app format
            setPlayerData({
              playerId: profile.player_id,
              username: profile.name,
              name: profile.name,
              email: profile.email,
              countryCode: profile.country_code,
              handedness: profile.handedness || 'right',
              stats: {
                totalTraks: profile.total_traks || 0,
                totalTags: profile.total_tags || 0,
                questionsAnswered: profile.questions_answered || 0,
                correctAnswers: profile.correct_answers || 0,
                currentStreak: profile.current_streak || 0,
                lastPlayDate: profile.last_play_date,
                claimedMilestones: profile.claimed_milestones || []
              }
            });
            
            // 🔧 FIX 2025.12.06: Check for missed milestones on profile load
            checkAndCreateMissedMilestones(
              profile.total_traks || 0,
              profile.claimed_milestones || []
            );
            
            // 🆕 Check for pending pal referral bonus on app load
            const token = getToken();
            if (token) {
              checkPalReferralBonus(token);
            }
            
            return;
          }
        } catch (err) {
          console.log('API auth failed, using local data');
        }
      }
      
      // Fallback to localStorage
      const savedPlayerData = localStorage.getItem('playerData');
      if (savedPlayerData) {
        try {
          const parsed = JSON.parse(savedPlayerData);
          const today = new Date().toDateString();
          const lastPlayDate = parsed.stats?.lastPlayDate;
          let newStreak = parsed.stats?.currentStreak || 0;
          
          if (lastPlayDate) {
            const lastPlay = new Date(lastPlayDate).toDateString();
            const yesterday = new Date(Date.now() - 86400000).toDateString();
            
            if (lastPlay === today) {
              // Same day
            } else if (lastPlay === yesterday) {
              newStreak = newStreak + 1;
            } else {
              newStreak = 1;
            }
          } else {
            newStreak = 1;
          }
          
          const updatedPlayerData = {
            ...parsed,
            lastLogin: new Date().toISOString(),
            stats: { ...parsed.stats, currentStreak: newStreak, lastPlayDate: today }
          };
          
          setPlayerData(updatedPlayerData);
          localStorage.setItem('playerData', JSON.stringify(updatedPlayerData));
        } catch (error) {
          console.error('Error loading player data:', error);
        }
      }
    };
    
    loadPlayerData();
  }, []);

  // Handle successful authentication
  const handleAuthSuccess = (player) => {
    if (player) {
      setIsAuthenticated(true);
      
      // 🔧 FIX 2026.01.17: Transfer ALL accumulated guest stats, not just current session
      const pending = pendingSessionStats;
      
      // Also check localStorage for accumulated stats from previous games
      let accumulatedStats = { totalTraks: 0, totalTags: 0, questionsAnswered: 0, correctAnswers: 0 };
      try {
        const savedPlayerData = localStorage.getItem('playerData');
        if (savedPlayerData) {
          const parsed = JSON.parse(savedPlayerData);
          if (parsed.stats) {
            accumulatedStats = {
              totalTraks: parsed.stats.totalTraks || 0,
              totalTags: parsed.stats.totalTags || 0,
              questionsAnswered: parsed.stats.questionsAnswered || 0,
              correctAnswers: parsed.stats.correctAnswers || 0
            };
            console.log('📊 Found accumulated guest stats in localStorage:', accumulatedStats);
          }
        }
      } catch (err) {
        console.error('Error reading accumulated guest stats:', err);
      }
      
      // Combine: accumulated localStorage stats + current session stats (avoid double counting)
      // If pendingSessionStats exists, it's already included in localStorage, so use the higher value
      const guestTraks = Math.max(accumulatedStats.totalTraks, pending?.traksEarned || 0);
      const guestTags = Math.max(accumulatedStats.totalTags, pending?.tagsEarned || 0);
      const guestQuestions = Math.max(accumulatedStats.questionsAnswered, pending?.questionsAnswered || 0);
      const guestCorrect = Math.max(accumulatedStats.correctAnswers, pending?.correctAnswers || 0);
      
      const hasGuestStats = guestTraks > 0 || guestTags > 0;
      
      setPlayerData({
        playerId: player.player_id,
        username: player.name,
        name: player.name,
        email: player.email,
        countryCode: player.country_code,
        handedness: player.handedness || 'right',
        stats: {
          // Add ALL guest stats to new player's totals
          totalTraks: (player.total_traks || 0) + guestTraks,
          totalTags: (player.total_tags || 0) + guestTags,
          questionsAnswered: (player.questions_answered || 0) + guestQuestions,
          correctAnswers: (player.correct_answers || 0) + guestCorrect,
          currentStreak: player.current_streak || 0,
          lastPlayDate: player.last_play_date,
          claimedMilestones: player.claimed_milestones || []
        }
      });
      
      // 🔧 FIX 2026.01.17: Sync ALL guest stats to database for new registration
      // 🔧 FIX 2026.01.18: Changed PUT to POST (server only has POST endpoint)
      if (hasGuestStats) {
        console.log('📊 Syncing ALL guest stats to new account:', { guestTraks, guestTags, guestQuestions, guestCorrect });
        const token = getToken();
        if (token) {
          fetch('https://api.traktag.com/api/players/stats', {
            method: 'POST',  // 🔧 FIX: Was PUT, but server only has POST
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              traks_earned: guestTraks,
              tags_earned: guestTags,
              questions_answered: guestQuestions,
              correct_answers: guestCorrect
            })
          }).then(response => {
            if (response.ok) {
              console.log('✅ All guest stats synced to database');
            } else {
              console.error('❌ Failed to sync guest stats - HTTP', response.status);
            }
          }).catch(err => {
            console.error('❌ Failed to sync guest stats:', err);
          });
        }
        // Clear pending stats
        setPendingSessionStats(null);
        // Clear localStorage guest stats (they're now in the database)
        localStorage.removeItem('playerData');
      }
      
      // Clear anonymous question count since they're now registered
      localStorage.removeItem('anonymousQuestionsPlayed');
      localStorage.removeItem('anonymousQuestionsPlayed');
      // Refresh reward badge after login
      setRefreshBadge(prev => prev + 1);
      
      // 🆕 Check for pending pal referral bonus (Spin to Win flow)
      const token = getToken();
      if (token) {
        checkPalReferralBonus(token);
      }
    } else {
      setIsAuthenticated(false);
    }
    setShowAuth(false);
  };

  // =========================================================================
  // 🎁 REWARD CLAIMING HANDLER (NEW)
  // =========================================================================
// =========================================================================
  // 🔧 FIX 2025.12.06: Check for missed milestones and create rewards
  // =========================================================================
 // =========================================================================
  // 🔧 FIX 2025.12.13: CRITICAL - Check if claimed BEFORE creating reward
  // Previous bug: Created reward first, then checked if claimed = duplicates!
  // =========================================================================
  const checkAndCreateMissedMilestones = async (totalTraks, claimedMilestones) => {
    const missed = getUnclaimedMilestones(totalTraks, claimedMilestones || []);
    if (missed.length === 0) {
      console.log('🏆 No missed milestones to catch up');
      return [];
    }
    
    const token = getToken();
    if (!token) {
      console.log('⚠️ Not logged in, skipping milestone catch-up');
      return [];
    }
    
    console.log(`🏆 Found ${missed.length} missed milestone(s) to catch up:`, missed.map(m => m.name));
    
    const newlyClaimed = [];
    for (const milestone of missed) {
      try {
        // 🔧 FIX 2025.12.13: Check if milestone is already claimed FIRST (before creating reward)
        const checkResponse = await fetch(`${API_URL}/profile/claim-milestone`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ milestone_traks: milestone.traks })
        });
        
        if (checkResponse.ok) {
          const data = await checkResponse.json();
          if (data.already_claimed) {
            console.log(`⚠️ Milestone ${milestone.name} was already claimed, skipping reward creation`);
            continue; // Skip to next milestone - don't create duplicate reward!
          }
          
          // Only NOW create the reward (after confirming it wasn't already claimed)
          await logAchievementReward(milestone.name, milestone.traks, milestone.bonusTraks);
          console.log(`🎁 Created reward for ${milestone.name}: +${milestone.bonusTraks} TRAKs`);
          
          newlyClaimed.push(milestone.traks);
          console.log(`✅ Milestone ${milestone.name} persisted to database`);
        } else {
          console.error('❌ Failed to check/persist milestone:', milestone.name, await checkResponse.text());
        }
      } catch (err) {
        console.error('❌ Error creating milestone reward:', err);
      }
    }
    
    return newlyClaimed;
  };

   const handleRewardsClaimed = async (newTotals) => {
    // Update local player state with new totals from claimed rewards
    if (newTotals) {
      const currentClaimedMilestones = playerData?.stats?.claimedMilestones || [];
      
      setPlayerData(prev => ({
        ...prev,
        stats: {
          ...prev.stats,
          totalTraks: newTotals.total_traks,
          totalTags: newTotals.total_tags
        }
      }));
      console.log('🎁 Rewards claimed! New totals:', newTotals);
      
      // 🔧 FIX 2025.12.06: Check for missed milestones after claiming rewards
      const newlyClaimed = await checkAndCreateMissedMilestones(
        newTotals.total_traks, 
        currentClaimedMilestones
      );
      
      if (newlyClaimed.length > 0) {
        setPlayerData(prev => ({
          ...prev,
          stats: {
            ...prev.stats,
            claimedMilestones: [...(prev.stats?.claimedMilestones || []), ...newlyClaimed]
          }
        }));
        console.log('🎁 Added missed milestones:', newlyClaimed);
      }
    }
    // Refresh the badge count
    setRefreshBadge(prev => prev + 1);
  };

  // =========================================================================
  // GAMES & CONTENT
  // =========================================================================
  const [games, setGames] = useState([]);

  const [pictures, setPictures] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [messages, setMessages] = useState([]);
  const [rewards, setRewards] = useState([]);

  // Load games from API
  useEffect(() => {
    const loadGames = async () => {
      try {
        const res = await fetch(`${API_URL}/games`);
        const data = await res.json();
        if (data && data.length > 0) {
          // Transform snake_case API fields to camelCase for frontend components
          const transformedGames = data.map(game => ({
            ...game,
            // Banner URLs (snake_case → camelCase)
            desktopBannerUrl: game.desktop_banner_url || game.desktopBannerUrl,
            mobileBannerUrl: game.mobile_banner_url || game.mobileBannerUrl,
            staticAdUrl: game.static_ad_url || game.staticAdUrl,
            exitAdUrl: game.exit_ad_url || game.exitAdUrl,
            interstitialAdUrl: game.interstitial_ad_url || game.interstitialAdUrl,
            adClickUrl: game.ad_click_url || game.adClickUrl,
            // Keep original snake_case for backward compatibility
            desktop_banner_url: game.desktop_banner_url,
            mobile_banner_url: game.mobile_banner_url,
            static_ad_url: game.static_ad_url,
            exit_ad_url: game.exit_ad_url,
            interstitial_ad_url: game.interstitial_ad_url,
            ad_click_url: game.ad_click_url
          }));
          setGames(transformedGames);
        }
      } catch (error) {
        console.error('Error loading games from API:', error);
        // Fallback to localStorage
        const savedGames = localStorage.getItem('games');
        if (savedGames) {
          try {
            const parsedGames = JSON.parse(savedGames);
            if (parsedGames?.length > 0) setGames(parsedGames);
          } catch (err) {
            console.error('Error loading games from localStorage:', err);
          }
        }
      }
    };
    loadGames();
  }, []);

  // 📨 Load notifications from API when authenticated
  useEffect(() => {
    const loadNotifications = async () => {
      if (!isAuthenticated) return;
      
      const token = getToken();
      if (!token) return;
      
      try {
        const res = await fetch(`${API_URL}/notifications`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.ok) {
          const data = await res.json();
          setNotifications(data || []);
          console.log('📨 Loaded', data?.length || 0, 'notifications from API');
        }
      } catch (error) {
        console.error('Error loading notifications:', error);
      }
    };
    
    loadNotifications();
    
    // Refresh notifications every 30 seconds
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);
// Load ALL pictures (lightweight) for thumbnails - single request
  useEffect(() => {
    const loadAllPictures = async () => {
      try {
        const res = await fetch(`${API_URL}/pictures`);
        const data = await res.json();
        if (data && data.length > 0) {
          setPictures(data);
          console.log(`🖼️ Loaded ${data.length} pictures for thumbnails`);
        }
      } catch (error) {
        console.error('Error loading pictures:', error);
      }
    };
    loadAllPictures();
  }, []);
    // Load questions from API when game is selected
  useEffect(() => {
    const loadQuestions = async () => {
      if (!selectedGameId) return;
      try {
        console.log(`📚 Loading questions for game ${selectedGameId} from ${API_URL}`);
        const res = await fetch(`${API_URL}/games/${selectedGameId}/questions`);
        const data = await res.json();
        console.log(`📚 Questions loaded:`, data?.length || 0, 'questions');
        if (Array.isArray(data) && data.length > 0) {
          setQuestions(data);
          console.log(`📚 Questions set in state:`, data[0]); // Log first question to verify structure
        } else {
          console.warn('📚 No questions returned or invalid data:', data);
          setQuestions([]);
        }
      } catch (error) {
        console.error('📚 Error loading questions from API:', error);
        setQuestions([]);
      }
    };
    loadQuestions();
  }, [selectedGameId]);

  // Load selected game from localStorage
  useEffect(() => {
    const savedGameId = localStorage.getItem('selectedGameId');
    if (savedGameId) setSelectedGameId(parseInt(savedGameId));
  }, []);

  // 🔍 SEO: Get location for route-based logic
  const location = useLocation();

  // 🔍 SEO: Dynamic canonical URL based on current page
  // NOTE: Quiz pages (/quiz/*) are handled by QuizSEO component via react-helmet
  useEffect(() => {
    const path = location.pathname;
    
    // Skip quiz pages - QuizSEO component handles canonical via react-helmet
    if (path.startsWith('/quiz/')) {
      console.log('🔍 SEO: Quiz page - canonical handled by QuizSEO component');
      return;
    }
    
    let canonicalUrl = 'https://traktag.com/';
    
    // Set canonical based on current route (non-quiz pages only)
    if (path === '/help') {
      canonicalUrl = 'https://traktag.com/help';
    } else if (path === '/terms') {
      canonicalUrl = 'https://traktag.com/terms';
    } else if (path === '/privacy') {
      canonicalUrl = 'https://traktag.com/privacy';
    } else if (path === '/gdpr') {
      canonicalUrl = 'https://traktag.com/gdpr';
    }
    
    // Update or create canonical link tag
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.href = canonicalUrl;
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      canonicalLink.href = canonicalUrl;
      document.head.appendChild(canonicalLink);
    }
    
    console.log('🔍 SEO: Canonical URL set to:', canonicalUrl);
  }, [location.pathname]);

  // 🔍 SEO: Handle /quiz/:slug URLs - load game by slug
  useEffect(() => {
    const loadGameBySlug = async () => {
      const path = window.location.pathname;
      const match = path.match(/^\/quiz\/([^/]+)$/);
      if (match && games.length > 0) {
        const slug = match[1];
        console.log('🔍 SEO: Loading game by slug:', slug);
        
        // First try to find game by slug in loaded games
        const gameBySlug = games.find(g => g.slug === slug);
        if (gameBySlug) {
          console.log('🔍 SEO: Found game:', gameBySlug.name);
          
          // 📂 Check if this is a parent page (has children, no questions)
          if (gameBySlug.is_parent) {
            console.log('📂 SEO: This is a parent category page');
            setParentGame(gameBySlug);
            return;
          }
          
          // Regular quiz - clear parent state and open quiz
          setParentGame(null);
          setSelectedGameId(gameBySlug.game_id);
          setActiveTab('quizzes'); // 🆕 Switch to quizzes tab
          localStorage.setItem('selectedGameId', gameBySlug.game_id.toString());
          // 🆕 Delay autoOpenGameId to ensure QuizzesTab is mounted
          setTimeout(() => {
            console.log('🔗 Setting autoOpenGameId:', gameBySlug.game_id);
            setAutoOpenGameId(gameBySlug.game_id);
          }, 100);
          return;
        }
        
        // If not found locally, try API
        try {
          const res = await fetch(`${API_URL}/games/slug/${slug}`);
          if (res.ok) {
            const game = await res.json();
            console.log('🔍 SEO: Found game from API:', game.name);
            
            // 📂 Check if this is a parent page
            if (game.is_parent) {
              console.log('📂 SEO: This is a parent category page (from API)');
              setParentGame(game);
              return;
            }
            
            // Regular quiz
            setParentGame(null);
            setSelectedGameId(game.game_id);
            setActiveTab('quizzes'); // 🆕 Switch to quizzes tab
            localStorage.setItem('selectedGameId', game.game_id.toString());
            // 🆕 Delay autoOpenGameId to ensure QuizzesTab is mounted
            setTimeout(() => {
              console.log('🔗 Setting autoOpenGameId:', game.game_id);
              setAutoOpenGameId(game.game_id);
            }, 100);
          } else {
            console.warn('🔍 SEO: Game not found for slug:', slug);
          }
        } catch (err) {
          console.error('🔍 SEO: Error loading game by slug:', err);
        }
      }
    };
    loadGameBySlug();
  }, [games]);

  // 🆕 2025.12.22: Handle ?game= URL parameter for sharing specific quizzes
  useEffect(() => {
    if (games.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const gameParam = urlParams.get('game');
      if (gameParam) {
        const gameId = parseInt(gameParam);
        const game = games.find(g => g.game_id === gameId);
        if (game) {
          console.log('🔗 Deep link: Loading game by ID:', game.name);
          setSelectedGameId(gameId);
          localStorage.setItem('selectedGameId', gameId.toString());
          setActiveTab('quizzes'); // Switch to quizzes tab
          // Clean up URL without reload
          window.history.replaceState({}, '', window.location.pathname);
          // 🆕 Delay autoOpenGameId to ensure QuizzesTab is mounted
          setTimeout(() => {
            console.log('🔗 Setting autoOpenGameId:', gameId);
            setAutoOpenGameId(gameId);
          }, 100);
        }
      }
    }
  }, [games]);

  // Auto-select first game if none selected
  useEffect(() => {
    if (selectedGameId === null && games.length > 0) {
      const firstGameId = games[0].game_id;
      setSelectedGameId(firstGameId);
      localStorage.setItem('selectedGameId', firstGameId.toString());
    }
  }, [selectedGameId, games]);

  // Picture Grid Settings
  const [pictureGridSettings, setPictureGridSettings] = useState(() => {
    const saved = localStorage.getItem('pictureGridSettings');
    return saved ? JSON.parse(saved) : {
      mode: 'A',
      adminMessage: '',
      pictureUrls: [],
      useTestPattern: false,
      enabledGames: []
    };
  });

  useEffect(() => {
    localStorage.setItem('pictureGridSettings', JSON.stringify(pictureGridSettings));
  }, [pictureGridSettings]);

  // Achievement state
  const [showAchievement, setShowAchievement] = useState(false);
  const [achievementData, setAchievementData] = useState(null);
  
  // 🔧 FIX 2025.12.04: Track previous TRAKs to only trigger milestones on INCREASE during GAMEPLAY
  const [previousTraks, setPreviousTraks] = useState(null);

  // Check for achievement milestones - ONLY when TRAKs increase during gameplay
  useEffect(() => {
    const totalTraks = playerData.stats.totalTraks;
    const claimedMilestones = playerData.stats.claimedMilestones || [];
    
    // 🔧 FIX 2025.12.04: Only check milestones while playing
    // This prevents false triggers when API loads player data on login
    if (!isPlaying) {
      // Update baseline but don't check milestones
      setPreviousTraks(totalTraks);
      return;
    }
    
    // 🔧 FIX: Skip milestone check on initial load
    if (previousTraks === null) {
      // First time - just record the value, don't check milestones
      setPreviousTraks(totalTraks);
      console.log('🏆 MILESTONE: Setting baseline TRAKs:', totalTraks);
      return;
    }
    
    // 🔧 FIX: Only check milestones if TRAKs INCREASED (not on load or decrease)
    if (totalTraks <= previousTraks) {
      setPreviousTraks(totalTraks);
      return;
    }
    
    console.log('🏆 MILESTONE: TRAKs increased from', previousTraks, 'to', totalTraks);
    setPreviousTraks(totalTraks);
    
    for (let milestone of ACHIEVEMENT_MILESTONES) {
      if (totalTraks >= milestone.traks && !claimedMilestones.includes(milestone.traks)) {
        console.log('🏆 MILESTONE UNLOCKED:', milestone.name, 'at', milestone.traks, 'TRAKs');
        setAchievementData(milestone);
        setShowAchievement(true);
        
        // 🔧 FIX 2026.01.18: DON'T add bonus here - bonus is added when reward is CLAIMED
        // This was causing double bonus: once here, once when claiming from RewardClaimModal
        setPlayerData(prev => ({
          ...prev,
          stats: {
            ...prev.stats,
            // totalTraks stays the same - bonus will be added when reward is claimed
            claimedMilestones: [...prev.stats.claimedMilestones, milestone.traks]
          }
        }));
        
        // 🎁 Refresh reward badge after achievement (in case it was logged as claimable)
        setRefreshBadge(prev => prev + 1);
        break;
      }
    }
  }, [playerData.stats.totalTraks, isPlaying]);

  // =========================================================================
  // HANDLERS
  // =========================================================================
  
  const handleSelectGame = (gameId) => {
    setSelectedGameId(gameId);
    localStorage.setItem('selectedGameId', gameId.toString());
  };

  const handleSelectMode = (mode) => {
    setCurrentMode(mode);
  };

const handleStartPlayerMode = async () => {
    if (!selectedGameId) {
      alert('Please select a quiz first!');
      return;
    }
    // Clear saved grid state to start fresh
    localStorage.removeItem('currentPictureGrid');
    localStorage.removeItem('picturesSinceLast100');
    setGridTappedCells(new Set());
    setCurrentPictureIndex(0);
    setGroupQuizMode(false);
    
    // 🔧 FIX 2026.01.15: Lazy load pictures only for selected game
    try {
      const res = await fetch(`${API_URL}/games/${selectedGameId}/pictures`);
      const data = await res.json();
      if (data && data.length > 0) {
        setPictures(data);
        console.log(`🖼️ Loaded ${data.length} pictures for game ${selectedGameId}`);
      }
    } catch (error) {
      console.error('Error loading pictures:', error);
    }
    
    setIsPlaying(true);
  };

const handleStartGroupMode = async () => {
    if (!selectedGameId) {
      alert('Please select a quiz first!');
      return;
    }
    // Clear saved grid state to start fresh
    localStorage.removeItem('currentPictureGrid');
    localStorage.removeItem('picturesSinceLast100');
    setGridTappedCells(new Set());
    setCurrentPictureIndex(0);
    setGroupQuizMode(true);
    
    // 🔧 FIX 2026.01.15: Lazy load pictures only for selected game
    try {
      const res = await fetch(`${API_URL}/games/${selectedGameId}/pictures`);
      const data = await res.json();
      if (data && data.length > 0) {
        setPictures(data);
        console.log(`🖼️ Loaded ${data.length} pictures for game ${selectedGameId}`);
      }
    } catch (error) {
      console.error('Error loading pictures:', error);
    }
    
    setIsPlaying(true);
  };

  const handleExitGame = (sessionStats = null) => {
    // 🔧 FIX 2025.12.04: Update LOCAL playerData state with session stats
    if (sessionStats) {
      console.log('📊 Session stats received:', sessionStats);
      
      // Update local state so Stats tab shows correct values
      setPlayerData(prev => {
        const updated = {
          ...prev,
          stats: {
            ...prev.stats,
            totalTraks: (prev.stats.totalTraks || 0) + (sessionStats.traksEarned || 0),
            totalTags: (prev.stats.totalTags || 0) + (sessionStats.tagsEarned || 0),
            questionsAnswered: (prev.stats.questionsAnswered || 0) + (sessionStats.questionsAnswered || 0),
            correctAnswers: (prev.stats.correctAnswers || 0) + (sessionStats.correctAnswers || 0),
            lastPlayDate: new Date().toISOString()
          }
        };
        console.log('📊 Updated playerData.stats:', updated.stats);
        
        // Also save to localStorage for persistence
        localStorage.setItem('playerData', JSON.stringify(updated));
        
        return updated;
      });
      
      // Sync to database if logged in
      if (isLoggedIn()) {
        syncStatsToDatabase(
          sessionStats.traksEarned || 0,
          sessionStats.tagsEarned || 0,
          sessionStats.questionsAnswered || 0,
          sessionStats.correctAnswers || 0
        );
      }
    }
    
    // 🎁 Refresh reward badge when exiting game (may have earned claimable rewards)
    setRefreshBadge(prev => prev + 1);
    
    setIsPlaying(false);
    setActiveTab('home');
    setGridTappedCells(new Set());
    setQuestionsInSession(0);
  };

  const handleUpdateHandedness = async (hand) => {
    const updated = { ...playerData, handedness: hand };
    setPlayerData(updated);
    localStorage.setItem('playerData', JSON.stringify(updated));
    
    // Sync with API if logged in
    const token = getToken();
    if (token) {
      try {
        await fetch(`${API_URL}/players/settings`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ handedness: hand })
        });
        console.log('✅ Handedness synced to database');
      } catch (err) {
        console.error('Failed to sync handedness:', err);
      }
    }
  };

  const handleMarkNotificationRead = async (notificationId) => {
    // Update local state immediately
    setNotifications(prev => prev.map(n => 
      n.id === notificationId ? { ...n, read: true } : n
    ));
    
    // Also update in API
    const token = getToken();
    if (token) {
      try {
        await fetch(`${API_URL}/notifications/${notificationId}/read`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (err) {
        console.error('Error marking notification read:', err);
      }
    }
  };

  // Challenge handler
  const handleSubmitChallenge = async (challengeData) => {
    try {
      const response = await fetch(`${API_URL}/challenges`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question_id: challengeData.questionId,
          game_id: challengeData.gameId,
          player_id: playerData?.playerId || null,
          question_text: challengeData.questionText,
          player_answer: challengeData.playerAnswer,
          correct_answer: challengeData.correctAnswer,
          issue_type: challengeData.issueType,
          player_comments: challengeData.playerComments
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Challenge submitted successfully:', result);
        // Add to local state for immediate feedback
        const newChallenge = {
          id: result.challenge_id,
          ...challengeData,
          status: 'pending',
          adminResponse: '',
          submittedAt: new Date().toISOString()
        };
        setChallenges(prev => [newChallenge, ...prev]);
      } else {
        console.error('❌ Failed to submit challenge:', await response.text());
        alert('Failed to submit challenge. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error submitting challenge:', error);
      alert('Failed to submit challenge. Please try again.');
    }
  };

  // Game CRUD handlers (for Admin)
  const handleCreateGame = (gameData) => {
    const newGame = {
      game_id: Date.now(),
      ...gameData,
      active: true
    };
    const updated = [...games, newGame];
    setGames(updated);
    localStorage.setItem('games', JSON.stringify(updated));
  };

  const handleEditGame = (game) => {
    setEditingGame(game.game_id);
    setEditGameData({ ...game });
  };

  const handleSaveEditGame = () => {
    if (!editGameData?.name?.trim()) return;
    const updated = games.map(g => g.game_id === editGameData.game_id ? editGameData : g);
    setGames(updated);
    localStorage.setItem('games', JSON.stringify(updated));
    setEditingGame(false);
    setEditGameData(null);
  };

  const handleDeleteGame = (game) => {
    if (window.confirm('Delete game?')) {
      const updated = games.filter(g => g.game_id !== game.game_id);
      setGames(updated);
      localStorage.setItem('games', JSON.stringify(updated));
    }
  };

  const handleToggleGameStatus = (game) => {
    const updated = games.map(g => g.game_id === game.game_id ? { ...g, active: !g.active } : g);
    setGames(updated);
    localStorage.setItem('games', JSON.stringify(updated));
  };

  // Set a game as the Featured Quiz (unchecks all others)
  const handleSetFeaturedGame = (game) => {
    const updated = games.map(g => ({
      ...g,
      featured: g.game_id === game.game_id ? !g.featured : false
    }));
    setGames(updated);
    localStorage.setItem('games', JSON.stringify(updated));
  };

  // Picture handlers
  const handleUploadPicture = (gameId, pictureData) => {
    const newPicture = {
      pic_id: Date.now(),
      game_id: gameId,
      display_order: pictures.filter(p => p.game_id === gameId).length,
      ...pictureData
    };
    const updated = [...pictures, newPicture];
    setPictures(updated);
    localStorage.setItem('pictures', JSON.stringify(updated));
  };

  const handleUpdatePicture = (picId, updatedData) => {
    const updated = pictures.map(p => p.pic_id === picId ? { ...p, ...updatedData } : p);
    setPictures(updated);
    localStorage.setItem('pictures', JSON.stringify(updated));
  };

  const handleDeletePicture = (pic) => {
    if (window.confirm('Delete picture?')) {
      const updated = pictures.filter(p => p.pic_id !== pic.pic_id);
      setPictures(updated);
      localStorage.setItem('pictures', JSON.stringify(updated));
    }
  };

  const handleMovePicture = (pic, direction) => {
    // Implementation same as before
  };

  // Question handlers
  const handleAddQuestion = (gameId) => {
    const newQuestion = {
      question_id: Date.now(),
      game_id: gameId,
      question_text: 'New Question',
      correct_answer: 'Correct',
      wrong_answer_1: 'Wrong 1',
      wrong_answer_2: 'Wrong 2',
      wrong_answer_3: 'Wrong 3',
      tag_value: 10,
      active: true
    };
    setQuestions(prev => [...prev, newQuestion]);
  };

  const handleEditQuestion = (questionId, updatedQuestion) => {
    setQuestions(prev => prev.map(q => q.question_id === questionId ? updatedQuestion : q));
  };

  const handleDeleteQuestion = (q) => {
    if (window.confirm('Delete?')) {
      setQuestions(prev => prev.filter(qu => qu.question_id !== q.question_id));
    }
  };

  const handleToggleQuestionStatus = (q) => {
    setQuestions(prev => prev.map(qu => qu.question_id === q.question_id ? { ...qu, active: !qu.active } : qu));
  };

  const handleBulkUpload = (gameId, parsed) => {
    const newQuestions = parsed.map((q, i) => ({
      question_id: Date.now() + i,
      game_id: gameId,
      ...q,
      active: true
    }));
    setQuestions(prev => [...prev, ...newQuestions]);
    alert(`Imported ${parsed.length} questions!`);
  };

  // Message/Challenge handlers
  const handleApproveChallenge = (challenge, response) => {
    setChallenges(prev => prev.map(c => c.id === challenge.id ? { ...c, status: 'approved', adminResponse: response } : c));
    setPlayerData(prev => ({ ...prev, stats: { ...prev.stats, totalTraks: prev.stats.totalTraks + 50 } }));
  };

  const handleDeclineChallenge = (challenge, response) => {
    setChallenges(prev => prev.map(c => c.id === challenge.id ? { ...c, status: 'declined', adminResponse: response } : c));
  };

  const handleSendMessage = (messageData) => {
    const newMessage = { id: Date.now(), ...messageData, timestamp: new Date().toISOString() };
    setMessages(prev => [newMessage, ...prev]);
  };

  const handleAwardReward = (player, amount, reason) => {
    setPlayerData(prev => ({ ...prev, stats: { ...prev.stats, totalTraks: prev.stats.totalTraks + amount } }));
    setRewards(prev => [...prev, { id: Date.now(), amount, reason, timestamp: new Date().toISOString() }]);
  };

  // Get selected game object
  const selectedGame = selectedGameId ? games.find(g => g.game_id === selectedGameId) : null;
  const unreadNotifications = notifications.filter(n => !n.read).length;

  // =========================================================================
  // RENDER
  // =========================================================================
  
  // Handle login required for PalHelpPage
  const handlePalLoginRequired = (mode) => {
    setAuthMode(mode);
    setShowAuth(true);
  };
  
  // Show Auth Screen if requested
  if (showAuth) {
    return (
      <AuthScreen
  onAuthSuccess={handleAuthSuccess}
  playerData={isAuthenticated ? playerData : null}
  initialMode={authMode}
/>
    );
  }

  return (
    <Routes>
      {/* Pal Help Page - viral loop landing */}
      <Route 
        path="/pal/:code" 
        element={
          <PalHelpPage 
            isLoggedIn={isAuthenticated}
            playerData={playerData}
            onLoginRequired={handlePalLoginRequired}
          />
        } 
      />
      
      {/* Main App */}
      <Route path="*" element={
    <div className="min-h-screen bg-gray-900">
      
      {/* 🔍 SEO: Dynamic meta tags for quiz pages */}
      {selectedGame && (
        <QuizSEO 
          quiz={selectedGame} 
          questionCount={questions.filter(q => q.game_id === selectedGame.game_id).length}
        />
      )}
      {selectedGame && (
        <QuizSEOContent 
          quiz={selectedGame}
          questionCount={questions.filter(q => q.game_id === selectedGame.game_id).length}
        />
      )}
      
      {/* ================================================================= */}
      {/* MAIN CONTENT AREA - Based on active tab or playing state */}
      {/* ================================================================= */}
      
      {/* 📂 Parent Category Page - shows child quizzes */}
      {parentGame ? (
        <ParentQuizPage
          parentGame={parentGame}
          onSelectChildQuiz={(quiz) => {
            setParentGame(null);
            setSelectedGameId(quiz.game_id);
            setActiveTab('quizzes');
            localStorage.setItem('selectedGameId', quiz.game_id.toString());
            setTimeout(() => setAutoOpenGameId(quiz.game_id), 100);
            window.history.pushState({}, '', `/quiz/${quiz.slug}`);
          }}
        />
      ) : !isPlaying ? (
        <>
          {/* Login/Profile Button - Top Right */}
          <button
            onClick={() => setShowAuth(true)}
            className="fixed top-4 right-4 z-50 bg-gray-800 hover:bg-gray-700 text-white px-3 py-2 rounded-lg text-sm font-bold flex items-center gap-2"
          >
            {isAuthenticated ? (
              <>
                👤 {playerData.name || 'Profile'}
                {/* Red dot for unread notifications */}
                {unreadNotifications > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                    {unreadNotifications > 9 ? '9+' : unreadNotifications}
                  </span>
                )}
              </>
            ) : (
              <>🔐 Login</>
            )}
          </button>

          {/* Tab Content */}
          {activeTab === 'home' && (
            <HomeTab
              playerData={isAuthenticated ? playerData : null}
              isAuthenticated={isAuthenticated}
              games={games}
              pictures={pictures}
              selectedGameId={selectedGameId}
              onSelectGame={handleSelectGame}
              onStartGame={handleStartPlayerMode}
              onGoToQuizzes={() => setActiveTab('quizzes')}
              onStartGroupMode={handleStartGroupMode}
              onStartSoloMode={() => setGroupQuizMode(false)}
              onSelectMode={handleSelectMode}
              groupQuizTimer={groupQuizTimer}
              setGroupQuizTimer={setGroupQuizTimer}
              logoUrl="/TRAKtag_Logo_Full.png"
              iconUrl="/TRAKtag_Icon.png"
            />
          )}

          {activeTab === 'quizzes' && (
            <QuizzesTab
              games={games}
              pictures={pictures}
              questions={questions}
              selectedGameId={selectedGameId}
              onSelectGame={handleSelectGame}
              currentMode={currentMode}
              onSelectMode={handleSelectMode}
              onStartPlayerMode={handleStartPlayerMode}
              onStartGroupMode={handleStartGroupMode}
              groupQuizMode={groupQuizMode}
              groupQuizTimer={groupQuizTimer}
              setGroupQuizTimer={setGroupQuizTimer}
              autoOpenGameId={autoOpenGameId}
              clearAutoOpenGameId={() => setAutoOpenGameId(null)}
            />
          )}

         {activeTab === 'stats' && (
  <StatsTab playerData={playerData} isAuthenticated={isAuthenticated} />
)}

          {activeTab === 'settings' && (
            <SettingsTab
              playerData={playerData}
              onUpdateProfile={setPlayerData}
              onUpdateHandedness={handleUpdateHandedness}
              notifications={notifications}
              onMarkNotificationRead={handleMarkNotificationRead}
              pictureGridSettings={pictureGridSettings}
              onUpdatePictureGridSettings={setPictureGridSettings}
            />
          )}

          {/* Bottom Navigation Bar */}
          <BottomNavBar
            activeTab={activeTab}
            onTabChange={setActiveTab}
            unreadNotifications={unreadNotifications}
          />

          {/* ============================================================= */}
          {/* 🎁 REWARD CLAIMING COMPONENTS (NEW) */}
          {/* ============================================================= */}
          
          {/* Floating Unclaimed Rewards Badge */}
          <UnclaimedRewardsBadge
            isAuthenticated={isAuthenticated}
            onClick={() => setShowClaimModal(true)}
            refreshTrigger={refreshBadge}
            position="bottom-right"
            size="normal"
          />

        </>
      ) : (
        /* ================================================================= */
        /* GAME PLAYING SCREEN */
        /* ================================================================= */
        <div className="min-h-screen">
          {/* Exit Button */}
          <button
            onClick={handleExitGame}
            className="fixed top-4 left-4 z-[9999] bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg font-bold flex items-center gap-2"
          >
            🏠 Exit
          </button>

          {/* Game Component */}
          <Section2WithIDLabels 
            onSubmitChallenge={handleSubmitChallenge}
            playerData={playerData}
            currentMode={currentMode}
            modeStats={modeStats}
            setModeStats={setModeStats}
            groupQuizMode={groupQuizMode}
            setGroupQuizMode={setGroupQuizMode}
            groupQuizTimer={groupQuizTimer}
            setGroupQuizTimer={setGroupQuizTimer}
            questionsInSession={questionsInSession}
            setQuestionsInSession={setQuestionsInSession}
            selectedGame={selectedGame}
            selectedGameId={selectedGameId}
            games={games}
            pictures={pictures}
            questions={questions}
            pictureGridSettings={pictureGridSettings}
            gridTappedCells={gridTappedCells}
            setGridTappedCells={setGridTappedCells}
            currentPictureIndex={currentPictureIndex}
            setCurrentPictureIndex={setCurrentPictureIndex}
            compactLogoUrl="/TRAKtag_Icon.png"
            onExitGame={handleExitGame}
            isLoggedIn={isLoggedIn()}
            onShowAuth={(mode, sessionStats) => { 
              // 🔧 FIX 2025.12.12: Store session stats for guest registration
              if (sessionStats) {
                console.log('💾 Storing guest session stats for registration:', sessionStats);
                setPendingSessionStats(sessionStats);
              }
              setAuthMode(mode || 'login'); 
              setShowAuth(true); 
            }}
          />
        </div>
      )}

      {/* Achievement Celebration Popup */}
      {showAchievement && achievementData && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-80">
          <div className="bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 rounded-xl p-8 max-w-md mx-4 text-center border-8 border-yellow-300 shadow-2xl animate-pulse">
            <div className="text-9xl mb-4">{achievementData.icon}</div>
            <div className="text-white text-4xl font-black mb-2">🎉 ACHIEVEMENT UNLOCKED! 🎉</div>
            <div className="text-white text-3xl font-bold mb-4">{achievementData.name}</div>
            <div className="bg-white bg-opacity-20 rounded-lg p-4 mb-4">
              <p className="text-yellow-100 text-xl font-bold">Reward Available:</p>
              <p className="text-white text-3xl font-black">+{achievementData.bonusTraks} TRAKs!</p>
              <p className="text-yellow-100 text-sm mt-1">Claim in Rewards Center</p>
            </div>
            <button
              onClick={() => setShowAchievement(false)}
              className="bg-white text-orange-600 font-black text-xl px-8 py-4 rounded-lg hover:bg-yellow-100"
            >
              AWESOME! 🚀
            </button>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* 🎁 REWARD CLAIM MODAL (NEW) */}
      {/* ================================================================= */}
      <RewardClaimModal
        isOpen={showClaimModal}
        onClose={() => setShowClaimModal(false)}
        onClaim={handleRewardsClaimed}
        isAuthenticated={isAuthenticated}
      />

      {/* ================================================================= */}
      {/* 💬 FLOATING CONTACT BUTTON (always visible) */}
      {/* ================================================================= */}
      <FloatingContactButton
        playerData={isAuthenticated ? playerData : null}
        selectedGame={selectedGame}
      />

    </div>
      } />
    </Routes>
  );
}
